/**
 * 작성자 : 홍선영
 * 날짜 : 2023.03.10
 * 기능 : 옵션리스트에서 값을 선택해서 setState 하는 멀티 셀렉트박스 컴포넌트
 */

import React, { SetStateAction, useEffect, useState, Dispatch, useRef } from 'react';
import { SelectBoxSmDropStyle } from '../assets/styles/SelectBoxSmDrop';
import { v1 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Portal from './Portal';
import styled from 'styled-components';

interface OptionListProps {
  optionHeight?: 'height-lg' | 'height-md' | 'height-base' | 'height-sm';
}

const OptionList = styled.div<OptionListProps>`
  position: absolute;
  min-width: 200px;
  overflow-y: auto;
  background: var(--board);
  border: 1px solid var(--outline);
  border-radius: 0.25rem;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  pointer-events: auto;
  z-index: 9999;

  ${(props) => {
    switch (props.optionHeight) {
      case 'height-sm':
        return 'max-height: 12rem;';
      case 'height-base':
        return 'max-height: 16rem;';
      case 'height-md':
        return 'max-height: 20rem;';
      case 'height-lg':
        return 'max-height: 24rem;';
      default:
        return 'max-height: 16rem;';
    }
  }}

  li {
    font-size: 0.875rem;
    flex-shrink: 0;
    width: 100%;
    padding: 0.8rem 0.5rem;
    overflow: visible;
    white-space: break-spaces;
    height: fit-content;
    line-height: 1rem;
    display: flex;
    align-items: center;
    cursor: pointer;
    background-color: transparent;

    > span {
      width: 100% !important;
    }

    color: var(--text-secondary);
    &:hover:not(li.searchBox, li.selectedOption) {
      color: var(--text-primary);
      background-color: var(--tonal);
    }
  }

  .selectedOption,
  li.selectedOption:hover {
    color: var(--selected-primary);
    background-color: var(--tonal-blue);
    border-radius: 0;
    font-weight: 500;
  }

  input[type='checkbox'] {
    margin-right: 0.5rem;
  }
`;

interface ISearchSelectBoxMulti {
  options: any; // 셀렉트박스 배열 [{ 키네임1: 코드값1 }, { 키네임2: 코드값2 }, { 키네임3: 코드값3 }]
  defaultOption: string; // 셀렉트 디폴트옵션
  state: any[]; //  state값
  setState: Dispatch<SetStateAction<any[]>>; //  useState set액션
  stateKey: string; //  state 키값
  codeKey: string;
  disabled?: boolean;
  getBorderStyle?: any; // 선택값이 없을 때 보더 스타일
  filterbar?: 'filter-1-left' | 'filter-1-leftToCenter' | 'filter-1-center' | 'filter-1-centerToRight' | 'filter-1-right' | 'filter-2-left' | 'filter-2-center' | 'filter-2-right';
  align?: 'left' | 'right';
  dropDownWidth?: 'fit-content' | 'expand-content' | 'expand-content-md' | 'expand-content-sm' | 'expand-content-xs';
  asideMain?: boolean;
  comboWidth?: 'expand-box' | 'expand-box-md' | 'expand-box-sm';
  optionHeight?: 'height-lg' | 'height-md' | 'height-base' | 'height-sm';
  clickAble?: boolean; // 클릭 시 드롭다운 가능여부 (true: 드롭다운 안됨, false: 드롭다운 됨)
  toastContent?: string; // 드롭다운 안되는 경우 보여줄 토스트 메시지
  onClick?: () => void;
  optionType?: 'normal' | 'color'; // 옵션 타입 (normal: 일반, color: 색상 표시)
}

const SelectBoxMulti = ({
  options,
  defaultOption,
  state,
  setState,
  stateKey,
  codeKey,
  disabled,
  getBorderStyle,
  filterbar,
  dropDownWidth = 'fit-content',
  align = 'left',
  asideMain = false,
  comboWidth,
  optionHeight,
  clickAble,
  toastContent,
  onClick,
  optionType = 'normal',
}: ISearchSelectBoxMulti) => {
  const { t } = useTranslation();
  const selRef = useRef<HTMLLIElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({
    width: '80px',
    top: -1000,
    left: -1000,
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<{ type: string; [key: string]: string | undefined; cdName: string; backColor?: string; fontColor?: string }[]>(options || []);
  const prevStateRef = useRef<any[]>(state);
  const isInternalUpdateRef = useRef(false);

  useEffect(() => {
    if (options !== undefined && options !== null && options.length > 0) {
      setSelectedOption(options);
    } else if (options === null || options === undefined) {
      setSelectedOption([]);
    }
  }, [options]);

  // state prop이 변경될 때 selectedOption 동기화 (초기화 등 외부에서 state가 변경될 때)
  useEffect(() => {
    // 내부 업데이트로 인한 변경은 무시
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      prevStateRef.current = state;
      return;
    }

    if (Array.isArray(state)) {
      // 이전 state와 비교하여 실제로 변경되었는지 확인
      const prevStateKeys =
        prevStateRef.current
          ?.map((item: any) => item?.[stateKey])
          .filter(Boolean)
          .sort()
          .join(',') || '';
      const currentStateKeys = state
        .map((item: any) => item[stateKey])
        .filter(Boolean)
        .sort()
        .join(',');

      // 이전 state와 다를 때만 업데이트
      if (prevStateKeys !== currentStateKeys) {
        if (state.length === 0) {
          setSelectedOption([]);
        } else {
          setSelectedOption(state);
        }
        prevStateRef.current = state;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, stateKey]);

  useEffect(() => {
    // 내부 업데이트 플래그 설정
    isInternalUpdateRef.current = true;
    setState([...selectedOption]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  // 콤보박스 바깥클릭 감지
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    const updateDropdownPosition = () => {
      if (selRef.current && wrapperRef.current) {
        const rect = selRef.current.getBoundingClientRect();
        const wRect = wrapperRef.current.getBoundingClientRect();

        const isOverWidth = rect.left + wRect.width > window.innerWidth;
        const isOverHeight = rect.top + rect.height + wRect.height + 5 > window.innerHeight;

        setDropdownStyle((prev) => ({
          ...prev,
          top: isOverHeight ? rect.bottom - rect.height - wRect.height - 5 : rect.bottom + 5,
          left: isOverWidth ? rect.left + rect.width - wRect.width : rect.left,
          width: rect.width,
        }));
      }
    };

    if (isDropdownOpen) {
      // 초기 위치 계산
      updateDropdownPosition();

      // 해상도 변경 이벤트 리스너 추가
      window.addEventListener('resize', updateDropdownPosition);

      // 스크롤 이벤트 리스너 추가
      window.addEventListener('scroll', updateDropdownPosition, true);
    }

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [isDropdownOpen]);

  // 콤보박스 열린상태에서 바깥 클릭했을 때 창닫기
  const handleClickOutside = (e: any) => {
    if (wrapperRef && !wrapperRef.current?.contains(e.target) && !selRef.current?.contains(e.target)) {
      setIsDropdownOpen(false);
    }
  };

  // 콤보박스 클릭시 옵션리스트 열기
  const onClickDropdown = () => {
    if (clickAble && toastContent) {
      toast.warn(t(toastContent));
      return;
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  //  기본메뉴배열 노출
  const renderAllDropdownMenu = () => {
    if (Array.isArray(options)) {
      return options?.map((el: any) => (
        <li
          className={selectedOption?.find((item) => item[stateKey] === el[stateKey]) ? 'selectedOption' : undefined}
          key={v1()}
          role='presentation'
          onClick={() => {
            if (selectedOption?.find((item) => item[stateKey] === el[stateKey])) {
              // 전체 옵션 체크 해제 시 모든 옵션 해제
              if (el[stateKey] === 'A' || el[stateKey] === '') {
                setSelectedOption([]);
              } else {
                // 개별 항목 uncheck 시 전체 옵션이 있으면 함께 제거
                setSelectedOption((prev) => {
                  const filtered = (prev || []).filter((item) => item[stateKey] !== el[stateKey] && item[stateKey] !== 'A' && item[stateKey] !== '');
                  return filtered;
                });
              }
            } else if (el[stateKey] === 'A' || el[stateKey] === '') {
              // 전체 옵션 선택 시 모든 옵션 선택
              setSelectedOption(options);
            } else {
              // 다른 옵션 선택 시 전체 옵션이 있으면 제거하고 선택
              setSelectedOption((prev) => {
                const filtered = prev.filter((item) => item[stateKey] !== 'A' && item[stateKey] !== '');
                return [...filtered, el];
              });
            }
          }}
        >
          <input type='checkbox' checked={!!selectedOption?.find((item) => item[stateKey] === el[stateKey])} readOnly />
          {optionType === 'color' && el.backColor && el.fontColor ? (
            <span style={{ backgroundColor: el.backColor, color: el.fontColor, padding: '0.25rem 0.5rem', borderRadius: '0.25rem', display: 'inline-block' }}>{el[codeKey]}</span>
          ) : (
            el[codeKey]
          )}
        </li>
      ));
    }
    return null;
  };

  const handleClick = () => {
    onClickDropdown();
    onClick && onClick();
  };

  const emptyData = options?.length === 0;

  const renderCdName = () => {
    if (emptyData) return `${t('목록 없음')}`;
    if (selectedOption && selectedOption.length > 0 && selectedOption[0][codeKey] !== undefined && state.find((item) => item[stateKey] !== '' && item[stateKey] !== 'A')) {
      const firstOption = selectedOption[0];
      const codeName = firstOption[codeKey];

      // 전체 옵션이 포함되어 있는지 확인 (stateKey가 'A'인 경우)
      const hasDefaultOption = selectedOption?.some((item) => item[stateKey] === 'A' || item[stateKey] === '') || false;

      // 전체 옵션이 포함되어 있으면 "전체"로만 표시 (커스텀 색상 적용 안 함)
      if (hasDefaultOption) {
        return t(defaultOption);
      }

      // 1개 이상 선택된 경우 커스텀 색상 적용 안 함
      if (selectedOption.length > 1) {
        return t(`${codeName || ''} 외 ${selectedOption.length - 1}개`);
      }

      // 정확히 1개만 선택된 경우에만 커스텀 색상 적용
      if (optionType === 'color' && firstOption.backColor && firstOption.fontColor) {
        return <span style={{ backgroundColor: firstOption.backColor, color: firstOption.fontColor, padding: '0.25rem 0.5rem', borderRadius: '3px' }}>{t(codeName || '')}</span>;
      }
      return t(codeName || '');
    }
    return t(defaultOption);
  };

  return (
    <SelectBoxSmDropStyle isDropdownOpen={isDropdownOpen}>
      <ul style={getBorderStyle}>
        <li className={`defaultOption ${emptyData ? 'disabled' : ''} ${isDropdownOpen ? 'opened' : ''} ${comboWidth}`} ref={selRef} role='presentation' onClick={handleClick}>
          <span style={optionType === 'color' && selectedOption && selectedOption.length > 0 && selectedOption[0]?.backColor && selectedOption[0]?.fontColor ? undefined : undefined}>
            {renderCdName()}
          </span>
          <div>
            <span className='material-symbols-rounded'>keyboard_arrow_down</span>
          </div>
        </li>
        {isDropdownOpen && !disabled && options?.length > 0 && (
          <Portal openModal>
            <OptionList ref={wrapperRef} style={dropdownStyle} optionHeight={optionHeight}>
              {renderAllDropdownMenu()}
            </OptionList>
          </Portal>
        )}
      </ul>
    </SelectBoxSmDropStyle>
  );
};

export default React.memo(SelectBoxMulti);
